import './ScheduleForm.scss';

import { IconAdd, IconAlertInfoOutlined } from '@hulu-react-style-components/icons';
import { bem } from 'mission-control-common-components';
import { useLayoutEffect } from 'react';

import Tooltip from '../../../../../../common/Tooltip';
import { CUSTOM_PACING_INFO_TOOLTIPS } from '../../constants';
import { useScheduleFormState } from '../../hooks/useScheduleFormState';
import { useScheduleRanges } from '../../hooks/useScheduleRanges';
import { getCPWindowDateFromPreviousDate } from '../../utils/form';
import ScheduleWindow from '../ScheduleWindow';

const SET_CP_SCHEDULE_MESSAGE = 'In this workflow, you may set up one or more custom pacing schedules.';
const ADD_SCHEDULE = 'add schedule';
const START_DATE_ADD_HOURS = 1;
const END_DATE_ADD_HOURS = 2;

const [block, element] = bem('set-cp-schedule-form');

type Props = {
  isDisabled: boolean;
};

const ScheduleForm = ({ isDisabled }: Props): React.JSX.Element => {
  const { resetRangeErrors } = useScheduleFormState();
  const {
    dateRanges,
    addRange,
    removeRange,
    canRemove,
    replaceRange,
    timezone,
    isDynamicEndDateEnabled,
    isDynamicEndDateSwitchDisabled,
    isDynamicStartDateEnabled,
    isDynamicStartDateSwitchDisabled,
    handleChangeIsDynamicEndDate,
    handleChangeIsDynamicStartDate,
  } = useScheduleRanges(resetRangeErrors, isDisabled);

  // effect is used to set default dates and enable calculation
  // calculation is enabled only when form is dirty
  useLayoutEffect(() => {
    if (dateRanges.length === 1 && !dateRanges[0]?.start && !dateRanges[0]?.end) {
      replaceRange(0, {
        ...dateRanges[0],
        start: getCPWindowDateFromPreviousDate(null, START_DATE_ADD_HOURS, timezone),
        end: getCPWindowDateFromPreviousDate(null, END_DATE_ADD_HOURS, timezone),
      });
    }
  }, [dateRanges, replaceRange, timezone]);

  return (
    <div className={block()}>
      <div>
        <p>{SET_CP_SCHEDULE_MESSAGE}</p>
        <table className={element('windows-table')}>
          <tbody>
            {dateRanges.map(({ id }, index) => (
              <ScheduleWindow
                key={id}
                index={index}
                onResetErrors={resetRangeErrors}
                onRemove={canRemove ? (): void => removeRange(index) : undefined}
                isDisabled={isDisabled}
                isDynamicStartDateEnabled={isDynamicStartDateEnabled}
                isDynamicEndDateEnabled={isDynamicEndDateEnabled}
                handleChangeIsDynamicEndDate={handleChangeIsDynamicEndDate}
                handleChangeIsDynamicStartDate={handleChangeIsDynamicStartDate}
                hasDynamicEndDate={index === dateRanges.length - 1}
                hasDynamicStartDate={index === 0}
                isDynamicEndDateSwitchDisabled={isDynamicEndDateSwitchDisabled}
                isDynamicStartDateSwitchDisabled={isDynamicStartDateSwitchDisabled}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className={element('add-schedule-wrapper')}>
        <button className={element('add-schedule')} onClick={addRange} disabled={isDisabled}>
          <IconAdd />
          <span>{ADD_SCHEDULE}</span>
        </button>
        <Tooltip id="add-schedule-tooltip" message={CUSTOM_PACING_INFO_TOOLTIPS.ADD_SCHEDULE} place="top">
          <IconAlertInfoOutlined
            className="info-icon"
            aria-label="add-schedule-info-icon"
            data-testid="add-schedule-info-icon"
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default ScheduleForm;
